/*
 *                     NOTICE
 *            COPYRIGHT 2021 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
import * as AbHooks from "abreact/utils/AbHooks";
import { when, autorun } from "mobx";
import * as React from "react";
/**
 * This is a React function component hook
 * Calls functionWithObservableResult() and returns the function's return value
 * In the future, when any mobx observables that were read during the function execution change,
 * the component using this hook will be forced to re-render.
 * @param functionWithObservableResult
 * @returns the result of calling functionWithObservableResult()
 */
export function useForceUpdateWhenObservedObservableChanges(functionWithObservableResult) {
  // closure state
  let firstTime = true;
  let calledPredicate = false;
  let result;
  let predicateException;
  let forceUpdate = AbHooks.useForceUpdate();
  let disposerRef = React.useRef(null); // place to hang disposer where we can get it back next time
  let resultRef = React.useRef(null); // track the last result

  // this handles cleanup of the mobx reaction when unmounting
  AbHooks.useOnUnmount(() => {
    disposerRef.current && disposerRef.current();
  });
  let predicate = () => {
    calledPredicate = true;
    if (firstTime) {
      try {
        result = functionWithObservableResult();
      } catch (err) {
        // Bubble up any exceptions thrown from the provided function, because
        // Mobx will catch them by default and not show them
        predicateException = err;
      }
      resultRef.current = result;
      firstTime = false;
      return false; // we don't want to exit the when yet
    }

    // so the second time this is called is after something observable changed
    // we want to exit the when and trigger the effect function
    return true;
  };
  let effect = () => {
    forceUpdate();
    disposerRef.current = null; // the when is going to stop observing, no need to dispose after firing effect
  };

  // clean up previous disposer
  disposerRef.current && disposerRef.current();

  // run predicate while observing, watch for change, remember disposer for cleanup
  disposerRef.current = when(predicate, effect);

  // Predicate is not called immediately if the when() executes inside a mobx action or runInAction
  if (!calledPredicate) {
    // Go ahead and call without observing to make sure we have a result.
    try {
      result = functionWithObservableResult();
    } catch (err) {
      // Bubble up any exceptions thrown from the provided function, because
      // Mobx will catch them by default and not show them
      predicateException = err;
    }

    // force update after we've exited out of current call stack, which exits action.
    // the force update runs clean in this timeout task. It will be inefficient if this happens a lot because every component will
    // get its own render cycle.
    setTimeout(() => {
      forceUpdate();
    }, 0);

    // abort the when()
    disposerRef.current();
    disposerRef.current = null;
  }
  if (predicateException !== undefined) {
    throw predicateException;
  }
  return result; // guaranteed to have a result here, just compiler can't sort it out because of when and calledPredicate flag
}

/**
 * Calls functionToObserve() and observes any mobx observables that are read by the function.
 * When any observed observable changes, calls functionToObserve() again (to re-observe) and also calls effect().
 * <br>
 * NOTE: if functionToObserve or effect change to a different object, it will start all over again, disposing of the old
 * reaction and starting a new one. This means you should not use plain lambdas that are created every time you
 * call this method, you should try to memoize them or otherwise keep them stable.
 * It can be complex to determine when to update the memo, but React.useMemo provides the mechanism.
 * <br>
 * When component unmounts, the mobx reaction is disposed.
 * @param functionToObserve
 * @param effect
 */
export function useCallEffectWithObservedFunctionChanges(functionToObserve, effect) {
  React.useEffect(() => {
    let firstTime = true;
    let disposer = autorun(() => {
      functionToObserve();
      if (firstTime) {
        firstTime = false;
      } else {
        effect();
      }
    });
    return () => {
      disposer();
    };
  }, [functionToObserve, effect]);
}

/**
 * Call functionToObserve() and observes any mobx observables that are read by the function.
 * Returns a data freshness index (incrementing counter) that updates when changes to observed observables occur.
 * When any observed observable changes, does a forceUpdate to make the calling component re-render.
 * On that next re-render, this hook then returns an incremented data freshness value.
 * <br>
 * Otherwise, repeated calls to this hook return the same freshness value.
 * <br>
 * When component unmounts, the mobx reaction is disposed.
 * <br>
 * NOTE: if functionToObserve changes to a different object, observing will start all over again, disposing of the old
 * reaction and starting a new one. This means you should not use plain lambdas that are created every time you
 * call this method, you should try to memoize them or otherwise keep them stable.
 * It can be complex to determine when to update the memo, but React.useMemo provides the mechanism.
 * @param functionToObserve
 */
export function useObserveFunctionAndUpdateDataFreshness(functionToObserve) {
  // when data freshness changes, really have to re-render for caller to see the change
  let forceUpdate = AbHooks.useForceUpdate();

  // the incrementer madness is to make sure we don't create a new lambda every time through, causing the effect hook to keep re-executing
  let dataFreshness = React.useRef({
    freshness: 0,
    incrementer: () => {
      dataFreshness.current.freshness += 1;
      forceUpdate();
    }
  });
  useCallEffectWithObservedFunctionChanges(functionToObserve, dataFreshness.current.incrementer);
  return dataFreshness.current.freshness;
}
//# sourceMappingURL=MobXReactUtils.js.map
